import { BloomFilter, optimal } from '@pacote/bloom-filter';
import { range, unique, windowed } from '@pacote/array';
import { queryTerms } from './query';
import { entries, keys, pick } from './object';
import { countIdf } from './tf-idf';
import { xxh64 } from '@pacote/xxhash';
import { memoize } from '@pacote/memoize';
import { findLast } from './array';
const compare = (a, b) => (a === b ? 0 : a > b ? -1 : 1);
const defaultTokenizer = (text) => text
    .normalize('NFD')
    .toLowerCase()
    .split(/[\s-]+/)
    .map((token) => token.replace(/\W/gi, ''));
/**
 * Encapsulates search functionality based on Bloom filters.
 */
export class BloomSearch {
    /**
     * Creates a new Bloom search instance based on Bloom filters, which can be
     * used to add documents and test the membership of search terms in the added
     * set.
     *
     * @param options Bloom search options.
     */
    constructor(options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        /**
         * Collection containing document summaries and Bloom filter signatures used
         * to search, with document shorthand reference identifier used as keys.
         */
        this.index = { version: 1, documents: {} };
        this.fields = Array.isArray(options.fields)
            ? options.fields.reduce((weight, field) => {
                weight[field] = 1;
                return weight;
            }, {})
            : options.fields;
        this.summary = options.summary;
        this.errorRate = (_a = options.errorRate) !== null && _a !== void 0 ? _a : 0.0001;
        this.ngrams = (_b = options.ngrams) !== null && _b !== void 0 ? _b : 1;
        this.minSize = (_c = options.minSize) !== null && _c !== void 0 ? _c : 0;
        this.termFrequencyBuckets = (_d = options.termFrequencyBuckets) !== null && _d !== void 0 ? _d : [
            1, 2, 3, 4, 8, 16, 32, 64,
        ];
        this.preprocess = (_e = options.preprocess) !== null && _e !== void 0 ? _e : String;
        this.stemmer = (_f = options.stemmer) !== null && _f !== void 0 ? _f : ((token) => token);
        this.stopwords = (_g = options.stopwords) !== null && _g !== void 0 ? _g : (() => true);
        this.tokenizer = (_h = options.tokenizer) !== null && _h !== void 0 ? _h : defaultTokenizer;
        this.seed = (_j = options.seed) !== null && _j !== void 0 ? _j : 0x00c0ffee;
        const toUint32 = (hex) => parseInt(hex.substring(8, 16), 16);
        const x1 = xxh64(this.seed + 1);
        const h1 = memoize(String, (data) => toUint32(x1.update(data).digest('hex')));
        const x2 = xxh64(this.seed + 2);
        const h2 = memoize(String, (data) => toUint32(x2.update(data).digest('hex')));
        this.hash = (i, data) => h1(data) + i * h2(data) + i ** 3;
    }
    /**
     * Replaces the instance's index with an index from another instance. Its
     * primary use case is to rehydrate the index from a static file or payload.
     *
     * **NB:** Calling this method will not change any other attributes in the
     * instance. It is up to developers to ensure that the instances were
     * initialised with compatible options, in particular the `stemmer`
     * function. Incompatible `stemmer` implementations may cause matches to
     * not be found in the rehydrated index.
     *
     * @param index Replacement index.
     */
    load(index) {
        if (index.version !== this.index.version) {
            throw new Error(`incompatible index schema version ${index.version}, expected ${this.index.version}`);
        }
        this.index.documents = entries(index.documents).reduce((acc, [ref, entry]) => {
            acc[ref] = {
                summary: entry.summary,
                signatures: entries(entry.signatures).reduce((signatures, [frequency, signature]) => {
                    signatures[frequency] = new BloomFilter({
                        ...signature,
                        hash: this.hash,
                    });
                    return signatures;
                }, {}),
            };
            return acc;
        }, {});
    }
    /**
     * Indexes a single document with its unique reference identifier.
     *
     * @param ref         - A unique reference identifier for the document. Adding
     *                      another document with the same reference replaces the
     *                      document on the search index.
     * @param document    - The document to index.
     * @param [language]  - Language identifier which is fed back into the
     *                      `stemmer` and `stopwords` callback functions to help
     *                      decide how to handle these steps.
     */
    add(ref, document, language) {
        const tokensByField = keys(this.fields).reduce((tokens, field) => {
            if (document[field] !== undefined) {
                const fieldText = this.preprocess(document[field], field, document);
                const fieldTokens = this.tokenizer(fieldText, language);
                const unigrams = fieldTokens
                    .filter((token) => token.length && this.stopwords(token, language))
                    .map((token) => this.stemmer(token, language));
                const ngrams = range(2, this.ngrams + 1)
                    .map((i) => windowed(i, fieldTokens).map((ngram) => ngram.join(' ')))
                    .flat();
                tokens.set(field, unigrams.concat(ngrams));
            }
            return tokens;
        }, new Map());
        const uniqueTokens = unique(Array.from(tokensByField.values()).flat());
        if (uniqueTokens.length === 0) {
            return;
        }
        const frequency = {};
        entries(this.fields).forEach(([field, weight = 1]) => {
            var _a;
            return ((_a = tokensByField.get(field)) !== null && _a !== void 0 ? _a : []).forEach((token) => {
                var _a;
                frequency[token] = ((_a = frequency[token]) !== null && _a !== void 0 ? _a : 0) + weight;
            });
        });
        const tokensByFrequency = Array.from(uniqueTokens).reduce((tokens, token) => {
            var _a;
            const frequencyBucket = (_a = findLast(this.termFrequencyBuckets, (limit) => frequency[token] >= limit)) !== null && _a !== void 0 ? _a : 0;
            if (!tokens[frequencyBucket]) {
                tokens[frequencyBucket] = [];
            }
            tokens[frequencyBucket].push(token);
            return tokens;
        }, {});
        const signatures = entries(tokensByFrequency).reduce((acc, [frequencyBucket, tokens]) => {
            acc[frequencyBucket] = new BloomFilter({
                ...optimal(Math.max(this.minSize, tokens.length), this.errorRate),
                seed: this.seed,
                hash: this.hash,
            });
            tokens.forEach((token) => {
                acc[frequencyBucket].add(token);
            });
            return acc;
        }, {});
        this.index.documents[ref] = {
            summary: pick(this.summary, document),
            signatures,
        };
    }
    /**
     * Removes an indexed document.
     *
     * @param ref - Reference identifier of the document to remove.
     */
    remove(ref) {
        delete this.index.documents[ref];
    }
    /**
     * Scans the document index and returns a list of documents summaries (with
     * only the properties declared in the `summary` option) that possibly match
     * one or more terms in the query.
     *
     * Each search term is run through the provided `stemmer` function to ensure
     * terms are processed in the same way as the tokens previously added to the
     * index's signature.
     *
     * @param query       - Terms to search.
     *
     * Individual words are matched against the signature of each indexed document.
     * You may prefix each word with the `+` operator to intersect results that
     * (probably) contain the required word, or use the `-` operator to exclude
     * results containing the word.
     *
     * If the `ngrams` option is greater than `1`, you are also able to search for
     * exact phrases up to `ngrams` words typed between quotes (for example,
     * `"this phrase"`). Only documents containing these words in that sequence
     * are returned in the search results.
     *
     * @param [language]  - Language identifier for the search terms. This is
     *                      used only to help choose the appropriate stemming
     *                      algorithm, search results will not filtered by
     *                      language.
     *
     * @returns Ordered list of document summaries, sorted by probable search
     *          term frequency.
     */
    search(query, language) {
        const tokens = this.parseQuery(query, language);
        const totalDocuments = keys(this.index.documents).length;
        return Object.values(this.index.documents)
            .filter((document) => tokens.excluded.every((token) => !this.hasToken(document, token)) &&
            tokens.required.every((token) => this.hasToken(document, token)))
            .map((document) => {
            const matches = {};
            for (const token of tokens.included) {
                matches[token] = this.hasToken(document, token);
            }
            return { summary: document.summary, matches };
        })
            .filter(({ matches }) => Object.values(matches).some((match) => match))
            .reduce((all, result, _, results) => {
            all.push({
                ...result,
                score: countIdf(result.matches, results.map(({ matches }) => matches), totalDocuments),
            });
            return all;
        }, [])
            .sort((a, b) => compare(a.score, b.score))
            .map(({ summary }) => summary);
    }
    hasToken(document, token) {
        var _a;
        return ((_a = keys(document.signatures).find((frequency) => document.signatures[frequency].has(token))) !== null && _a !== void 0 ? _a : 0);
    }
    parseQuery(query, language) {
        return queryTerms(query).reduce(({ required, included, excluded }, [term, type]) => {
            const token = type === 'phrase' ? term : this.stemmer(term, language);
            return {
                required: ['require', 'phrase'].includes(type)
                    ? required.concat(token)
                    : required,
                included: type !== 'exclude' ? included.concat(token) : included,
                excluded: type === 'exclude' ? excluded.concat(token) : excluded,
            };
        }, { required: [], included: [], excluded: [] });
    }
}
//# sourceMappingURL=index.js.map